import pick from 'lodash/pick';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import {
  withoutSquareVariantsOnListingImages,
  responseWithOurImageHost,
} from '../../util/imageVariants';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  transactionLineItems,
  initiateInquiry,
  getSeoLandingPages,
  queryMirrorReviews,
  reportMirrorShadow,
  searchMirrorListings,
  showMirrorListing,
} from '../../util/api';
import {
  compareSearchResults,
  mirrorModeOf,
  MIRROR_ON,
  MIRROR_SHADOW,
  MIRROR_WATCH_RATE,
} from '../SearchPage/SearchPage.duck';
import { seoCityOfAddress } from '../../util/seoLandings';
import * as log from '../../util/log';
import * as fbq from '../../lib/fpixel';
import { denormalisedResponseEntities } from '../../util/data';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { sanitizeMessage } from '../../util/sanitize';

const { UUID, LatLng } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const SEARCH_LISTINGS_REQUEST = 'app/ListingPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/ListingPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/ListingPage/SEARCH_LISTINGS_ERROR';

export const SIMILAR_LISTINGS_REQUEST = 'app/ListingPage/SIMILAR_LISTINGS_REQUEST';
export const SIMILAR_LISTINGS_SUCCESS = 'app/ListingPage/SIMILAR_LISTINGS_SUCCESS';
export const SIMILAR_LISTINGS_ERROR = 'app/ListingPage/SIMILAR_LISTINGS_ERROR';

export const SET_LISTING_CITY = 'app/ListingPage/SET_LISTING_CITY';
export const SET_SEO_LANDING_PAGES = 'app/ListingPage/SET_SEO_LANDING_PAGES';
export const SET_LISTING_PROVINCE = 'app/ListingPage/SET_LISTING_PROVINCE';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  // De qué anfitrión son las reseñas que hay en `reviews`. Lo usa `fetchReviews` para no
  // volver a pedirlas al hidratar: el servidor ya las trajo en el render.
  reviewsOfAuthorId: null,
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  savedListings: [],
  similarListingsRequest: false,
  similarListingsSucess: [],
  similarListingsError: null,
  listingCity: null,
  listingProvince: null,
  // Landings SEO del municipio del anuncio, para la miga de pan (`seoListingBreadcrumb`).
  seoLandingPages: [],
};

const resultIds = listings => listings.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...state, ...payload };

    case SHOW_LISTING_REQUEST:
      return {
        ...state,
        id: payload.id,
        showListingError: null,
        listingCity: null,
        listingProvince: null,
        seoLandingPages: [],
      };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload.reviews, reviewsOfAuthorId: payload.authorId };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };
    case SEARCH_LISTINGS_SUCCESS:
      return { ...state, savedListings: payload.data };
    case SIMILAR_LISTINGS_REQUEST:
      return { ...state, similarListingsRequest: true, similarListingsError: null };
    case SIMILAR_LISTINGS_SUCCESS:
      return {
        ...state,
        similarListingsRequest: false,
        similarListingsError: null,
        similarListingsSucess: payload,
      };
    case SIMILAR_LISTINGS_ERROR:
      return { ...state, similarListingsRequest: false, similarListingsError: payload };
    case SET_LISTING_CITY:
      return { ...state, listingCity: payload };
    case SET_LISTING_PROVINCE:
      return { ...state, listingProvince: payload };
    case SET_SEO_LANDING_PAGES:
      return { ...state, seoLandingPages: payload };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = (reviews, authorId) => ({
  type: FETCH_REVIEWS_SUCCESS,
  payload: { reviews, authorId },
});
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const fetchSimilarListingsRequest = () => ({ type: SIMILAR_LISTINGS_REQUEST });
export const fetchSimilarListingsSucess = listingIds => ({
  type: SIMILAR_LISTINGS_SUCCESS,
  payload: listingIds,
});
export const fetchSimilarListingsError = err => ({ type: SIMILAR_LISTINGS_ERROR, payload: err });

export const setListingCity = city => ({ type: SET_LISTING_CITY, payload: city });

/**
 * Landings que existen en el municipio del anuncio. Va en el HTML del servidor (el enlace es
 * para Google), así que `loadData` la espera; si falla, no hay miga de pan.
 */
const fetchSeoLandingPagesOfCity = (city, config) => dispatch => {
  const citySlug = seoCityOfAddress(city);
  if (!citySlug) {
    return Promise.resolve();
  }
  return getSeoLandingPages(citySlug, config?.ssrApiBaseUrl).then(pages =>
    dispatch({ type: SET_SEO_LANDING_PAGES, payload: pages })
  );
};
export const setListingProvince = province => ({ type: SET_LISTING_PROVINCE, payload: province });

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

export const searchPageListingsRequest = () => ({ type: SEARCH_LISTINGS_REQUEST });
export const searchPageListingsSuccess = listings => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: listings },
});
export const searchPageListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * Las variantes que la ficha pide y no dibuja, auditado el 3-11-2026 sobre
 * `ListingPageCoverPhoto` (la variante viva; `ListingPageCarousel` no se usa, y con él
 * `SectionGallery`, que era el único consumidor de `listing-card-4x` en todo el repo):
 *
 * - `scaled-small` y `scaled-medium`: el hero (`SectionHero`, la primera foto de la cara),
 *   el visor al abrirlo y las portadas de espacios interiores. **Se quedan en todas.**
 * - `listing-card-2x`: las `schemaImages` del JSON-LD, sobre todas las fotos de la cara.
 *   **Se queda en todas.**
 * - `facebook` y `twitter`: `og:image` y `twitter:image`. Solo las usa una foto, pero
 *   **cuál depende de la cara**: `faceView` ordena las del jardín por `gardenImageOrder` y
 *   salta las ocultas, así que la portada no es siempre `images[0]`. Se quedan en todas: el
 *   ahorro son 30 URLs de 144 y equivocarse rompe en silencio la vista previa de WhatsApp.
 * - `square-small` y `square-small2x`: solo `Avatar`. **Se quitan de las fotos de anuncio**
 *   con `withoutSquareVariantsOnListingImages`, igual que en la búsqueda.
 */

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.user': [
      'createdAt',
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.hasReceivedBooking',
      'profile.publicData.firstBookingDiscountOverride',
      'profile.publicData.averageRating',
      'profile.publicData.totalReviews',
      'profile.publicData.tiempo_respuesta',
    ],
    'fields.listing': [
      'title',
      'description',
      'geolocation',
      'price',
      'state',
      'createdAt',
      'availabilityPlan',
      'publicData.Regulation',
      'publicData.tag',
      'publicData.hostDetails',
      'publicData.score',
      'publicData.capacity',
      'publicData.extraItems',
      'publicData.capacityRanges',
      'publicData.extras',
      'publicData.priceRange',
      'publicData.searchListingType',
      'publicData.hasGarden',
      'publicData.poolMonths',
      'publicData.poolOffered',
      'publicData.gardenTitle',
      'publicData.gardenDescription',
      'publicData.gardenPriceRange',
      'publicData.gardenImageOrder',
      'publicData.hiddenImageIds',
      'publicData.gardenPoolUseExtra',
      // Espacios interiores: sección de la ficha y extras de la reserva (docs/indoor-spaces.md).
      'publicData.indoorSpaces',
      'publicData.listingType',
      'publicData.unitType',
      'publicData.firstBookingDiscountEnabled',
      'publicData.maxGuest',
      'publicData.minGuest',
      'publicData.minimumBookingHours',
      'publicData.minBookingPrice',
      'publicData.minAdvanceHours',
      'publicData.privacy',
      'publicData.poolMaintenance',
      'publicData.bathrooms',
      'publicData.ownerPresent',
      'publicData.transactionProcessAlias',
      'publicData.city',
      'publicData.province',
      // "Apto para lluvia / frío / sol" bajo "Equipamiento" (SectionWeatherProtectionMaybe), con
      // lo que declaró el anfitrión; la lluvia, con la misma regla que la búsqueda.
      'publicData.weatherProtectedFor',
      'publicData.weatherProtection',
      // La clave antigua del tipo de espacio: la regla de lluvia reconoce así los interiores.
      'publicData.category',
      // publicData.location intentionally NOT requested: the exact address is
      // author-only (privateData) and only revealed to a confirmed guest via
      // /api/listing-address (see docs/listing-location-privacy.md).
      'publicData.tiempo_respuesta',
      // Only for a closed listing: a garden merged into a pool listing redirects to it
      // (faceMergedIntoListingId).
      'metadata.closeReason',
      'metadata.faceMergedInto',
    ],

    // `listing-card-4x` fuera: no lo dibuja nadie (ver arriba). Las cuadradas se piden por el
    // avatar del autor y se quitan de las fotos de anuncio antes de guardarlas.
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.facebook',
      'variants.twitter',
      'variants.square-small',
      'variants.square-small2x',
    ],

    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  // The host's own draft or pending listing is not in the mirror (only published ones are).
  const show = isOwn
    ? sdk.ownListings.show(params)
    : mirrorOrSharetribe({
        mode: mirrorListingMode(),
        label: 'listing',
        params,
        config,
        fromSharetribe: () => sdk.listings.show(params),
        fromMirror: ssrApiBaseUrl => showMirrorListing(params, ssrApiBaseUrl),
        asQuery: r => ({ data: { data: [r.data.data], included: r.data.included } }),
      });

  return show
    .then(async data => {
      const listing = data.data.data;
      const listingId = listing.id.uuid;

      const city = listing?.attributes?.publicData?.city || '';
      const province = listing?.attributes?.publicData?.province || '';

      // Store city/province separately in Redux (before sanitization strips unconfigured fields)
      dispatch(setListingCity(city || null));
      dispatch(setListingProvince(province || null));

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      // 3️⃣ Store listing in Redux
      dispatch(addMarketplaceEntities(withoutSquareVariantsOnListingImages(data), sanitizeConfig));

      return data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

// Where the host's reviews come from (docs/mirror-search.md §8), `REACT_APP_MIRROR_REVIEWS_MODE`,
// same values as the search: off (Sharetribe), shadow (Sharetribe, and the browser compares with
// the mirror) or on (the mirror; Sharetribe if it cannot answer, and a sample keeps comparing).
export const mirrorReviewsMode = () => mirrorModeOf(process.env.REACT_APP_MIRROR_REVIEWS_MODE);

// Where the listing itself and its similar listings come from (docs/mirror-search.md §9),
// `REACT_APP_MIRROR_LISTING_MODE`, same values.
export const mirrorListingMode = () => mirrorModeOf(process.env.REACT_APP_MIRROR_LISTING_MODE);

/**
 * One read, from the mirror or from Sharetribe depending on `mode`. `fromMirror` gets the SSR base
 * URL only when it answers the user (the shadow comparison runs in the browser). `asQuery` turns a
 * response into the `listings.query` shape that `compareSearchResults` reads.
 */
const mirrorOrSharetribe = ({
  mode,
  label,
  params,
  config,
  fromSharetribe,
  fromMirror,
  asQuery = r => r,
}) => {
  const isBrowser = typeof window !== 'undefined';
  const compare = (fromSdk, mirrored, primary) =>
    reportMirrorShadow({
      ...compareSearchResults(asQuery(fromSdk), asQuery(mirrored)),
      primary,
      params: Object.keys(params).sort(),
    });

  if (mode === MIRROR_ON) {
    return fromMirror(config?.ssrApiBaseUrl).then(
      mirrored => {
        if (isBrowser && Math.random() < MIRROR_WATCH_RATE) {
          fromSharetribe()
            .then(fromSdk => compare(fromSdk, mirrored, 'mirror'))
            .catch(() => {});
        }
        return mirrored;
      },
      e => {
        console.warn(`[${label}] mirror unavailable, using Sharetribe:`, e.reason || e.message);
        return fromSharetribe();
      }
    );
  }
  return fromSharetribe().then(fromSdk => {
    if (mode === MIRROR_SHADOW && isBrowser) {
      fromMirror()
        .then(mirrored => compare(fromSdk, mirrored, 'sharetribe'))
        .catch(() => {});
    }
    return fromSdk;
  });
};

const queryHostReviews = (sdk, params, config) =>
  mirrorOrSharetribe({
    mode: mirrorReviewsMode(),
    label: 'reviews',
    params,
    config,
    fromSharetribe: () => sdk.reviews.query(params),
    fromMirror: ssrApiBaseUrl => queryMirrorReviews(params, ssrApiBaseUrl),
  });

export const fetchReviews = (listingId, authorId, config) => (dispatch, getState, sdk) => {
  // Al hidratar, el cliente vuelve a llamar a `loadData`: si el servidor ya trajo las reseñas
  // de este anfitrión, no se piden otra vez. Es el mismo patrón que ya usa `SeoLandingPage`.
  //
  // Se pueden saltar porque son de las pocas cosas de la ficha que no se quedan viejas entre
  // el render y la hidratación: una reseña nueva no aparece en ese segundo, y si apareciera
  // tampoco pasa nada. El precio y la disponibilidad sí se vuelven a pedir, como siempre.
  const authorIdStr = authorId?.uuid || (typeof authorId === 'string' ? authorId : null);
  const yaEstan = getState()?.ListingPage?.reviewsOfAuthorId;
  if (authorIdStr && yaEstan && yaEstan === authorIdStr) {
    return Promise.resolve();
  }

  dispatch(fetchReviewsRequest());

  return queryHostReviews(
    sdk,
    {
      subject_id: authorId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.user': [
        'createdAt',
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData.hasReceivedBooking',
        'profile.publicData.firstBookingDiscountOverride',
        'profile.publicData.averageRating',
        'profile.publicData.totalReviews',
        'profile.publicData.userType',
      ],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    },
    config
  )
    .then(response => {
      // Las reseñas no pasan por `marketplaceData`, que es donde se reescriben las URLs de
      // imagen: sin esto, los avatares viajaban al HTML (estado precargado) apuntando a
      // Sharetribe. Ver docs/sharetribe-credits.md.
      const allReviews =
        denormalisedResponseEntities({
          ...response,
          data: responseWithOurImageHost(response.data),
        }) || [];

      // Normalize listingId to a UUID string for comparisons
      const currentListingId =
        (typeof listingId === 'string' && listingId) ||
        listingId?.uuid ||
        listingId?.id?.uuid ||
        null;

      // Partition reviews: current listing first, then other listings
      const partitioned = allReviews.reduce(
        (acc, review) => {
          const reviewListingId = review?.listing?.id?.uuid || review?.listing?.id || null;
          if (currentListingId && reviewListingId === currentListingId) {
            acc.current.push(review);
          } else {
            acc.others.push(review);
          }
          return acc;
        },
        { current: [], others: [] }
      );

      // Sort each group by createdAt desc to keep newest first
      const sortByCreatedDesc = (a, b) =>
        new Date(b?.attributes?.createdAt || 0) - new Date(a?.attributes?.createdAt || 0);

      const currentListingReviews = partitioned.current.sort(sortByCreatedDesc);
      const otherListingReviews = partitioned.others.sort(sortByCreatedDesc);

      const orderedReviews = [...currentListingReviews, ...otherListingReviews];

      dispatch(fetchReviewsSuccess(orderedReviews, authorIdStr));
      return orderedReviews;
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

export const onFetchReviews = listingId => (dispatch, getState, sdk) => {
  return sdk.reviews.query({ listingId }).then(res => {
    const reviews = res?.data?.data;
    const reviewsLength = Array.isArray(reviews) && reviews.length;
    return reviewsLength;
  });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

export const sendInquiry = (listing, protectedData, message) => async (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId, protectedData: protectedData },
  };

  const sanitizedMessage = await sanitizeMessage(message);

  return initiateInquiry({ bodyParams })
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: sanitizedMessage }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));

        // metapixel event trigger
        fbq.event('Contact');

        // google analytics event trigger
        typeof window !== 'undefined' &&
          window &&
          window.gtag &&
          window.gtag('event', 'Contact', { app_name: 'cocopool', screen_name: 'inquire' });

        return transactionId;
      });
    })
    .catch(e => {
      // The server rejects inquiries from users who haven't verified their email
      // (anti-bot gate). Normalise it to a recognisable message so the form can
      // show a dedicated "verify your email" notice instead of a generic error.
      if (e?.error === 'email-not-verified') {
        dispatch(sendInquiryError(storableError(new Error('email-not-verified'))));
        throw e;
      }
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const unitType = attributes?.publicData?.unitType;
    const timeUnit = unitType === 'hour' ? 'hour' : 'day';
    const nextBoundary = findNextBoundary(new Date(), timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const showListingById = (listingId, config) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.user': [
      'createdAt',
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.hasReceivedBooking',
      'profile.publicData.firstBookingDiscountOverride',
      'profile.publicData.averageRating',
      'profile.publicData.totalReviews',
      'profile.publicData.tiempo_respuesta',
    ],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      // 'variants.scaled-large', // removed to reduce payload
      // 'variants.scaled-xlarge', // removed to reduce payload
      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      // `variants.${variantPrefix}-6x`, // removed to reduce payload
      // Social media
      'variants.facebook',
      'variants.twitter',
      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    'fields.listing': [
      'title',
      'description',
      'geolocation',
      'price',
      'createdAt',
      'availabilityPlan',
      'publicData.score',
      'publicData.Regulation',
      'publicData.tag',
      'publicData.hostDetails',
      'publicData.capacity',
      'publicData.extraItems',
      'publicData.capacityRanges',
      'publicData.extras',
      'publicData.priceRange',
      'publicData.searchListingType',
      'publicData.hasGarden',
      'publicData.poolMonths',
      'publicData.poolOffered',
      'publicData.gardenTitle',
      'publicData.gardenDescription',
      'publicData.gardenPriceRange',
      'publicData.gardenImageOrder',
      'publicData.hiddenImageIds',
      'publicData.gardenPoolUseExtra',
      // Espacios interiores: sección de la ficha y extras de la reserva (docs/indoor-spaces.md).
      'publicData.indoorSpaces',
      'publicData.listingType',
      'publicData.unitType',
      'publicData.firstBookingDiscountEnabled',
      'publicData.maxGuest',
      'publicData.minGuest',
      'publicData.minimumBookingHours',
      'publicData.minBookingPrice',
      'publicData.minAdvanceHours',
      'publicData.privacy',
      'publicData.poolMaintenance',
      'publicData.bathrooms',
      'publicData.ownerPresent',
      'publicData.transactionProcessAlias',
      'publicData.tiempo_respuesta',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    // ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio), // removed to reduce payload
  };

  return sdk.listings
    .show(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response.data.data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

export const queryListingbyIds = (geoLocation, listingIds, config) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  const myData = listingIds.map(id => {
    dispatch(showListingRequest(id.uuid));

    const params = {
      id: id.uuid,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.user': [
        'createdAt',
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData.hasReceivedBooking',
        'profile.publicData.firstBookingDiscountOverride',
        'profile.publicData.averageRating',
        'profile.publicData.totalReviews',
        'profile.publicData.tiempo_respuesta',
      ],
      'fields.image': [
        // Scaled variants for large images
        'variants.scaled-small',
        'variants.scaled-medium',
        // 'variants.scaled-large', // removed to reduce payload
        // 'variants.scaled-xlarge', // removed to reduce payload
        // Cropped variants for listing thumbnail images
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        // `variants.${variantPrefix}-6x`, // removed to reduce payload
        // Social media
        'variants.facebook',
        'variants.twitter',
        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'price',
        'createdAt',
        'availabilityPlan',
        'publicData.score',
        'publicData.Regulation',
        'publicData.tag',
        'publicData.hostDetails',
        'publicData.extraItems',
        'publicData.capacity',
        'publicData.capacityRanges',
        'publicData.extras',
        'publicData.priceRange',
        'publicData.searchListingType',
        'publicData.hasGarden',
        'publicData.poolMonths',
        'publicData.poolOffered',
        'publicData.gardenTitle',
        'publicData.gardenDescription',
        'publicData.gardenPriceRange',
        'publicData.gardenImageOrder',
        'publicData.hiddenImageIds',
        'publicData.gardenPoolUseExtra',
        // Espacios interiores: sección de la ficha y extras de la reserva (docs/indoor-spaces.md).
        'publicData.indoorSpaces',
        'publicData.listingType',
        'publicData.unitType',
        'publicData.firstBookingDiscountEnabled',
        'publicData.maxGuest',
        'publicData.minGuest',
        'publicData.minimumBookingHours',
        'publicData.minBookingPrice',
        'publicData.minAdvanceHours',
        'publicData.privacy',
        'publicData.poolMaintenance',
        'publicData.bathrooms',
        'publicData.ownerPresent',
        'publicData.transactionProcessAlias',
        'publicData.tiempo_respuesta',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
      // ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio), // removed to reduce payload
    };

    return sdk.listings
      .show(params)
      .then(response => {
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };
        dispatch(addMarketplaceEntities(response, sanitizeConfig));
        return denormalisedResponseEntities(response);
      })
      .then(res => {
        return res;
      })
      .catch(e => {
        dispatch(showListingError(storableError(e)));
      });
  });
  Promise.all(myData).then(res => dispatch(saveListingsfromSearchPage(res)));
};

export const saveListingsfromSearchPage = response => (dispatch, getState, sdk) => {
  dispatch(searchPageListingsRequest());

  const updatedArray = response.flat().slice(0, 5);

  // dispatch listings to success
  // const listingIds = listings.data.data.map(l => l.id);
  dispatch(searchPageListingsSuccess(updatedArray));
};

export const updateOwnListing = params => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  const queryParams = {
    expand: true,
  };

  return sdk.ownListings
    .update(params, queryParams)
    .then(res => {
      return res;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

export const fetchSimilarListingsOnLocation = (geoLocation, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchSimilarListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  const { lat, lng } = geoLocation || {};

  const queryParams = {
    origin: new LatLng(lat, lng),

    page: 1,
    perPage: 10,
    include: ['author', 'images'],
    'fields.user': [
      'createdAt',
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.hasReceivedBooking',
      'profile.publicData.firstBookingDiscountOverride',
      'profile.publicData.averageRating',
      'profile.publicData.totalReviews',
      'profile.publicData.userType',
      'profile.publicData.tiempo_respuesta',
    ],
    // Las `scaled-*` van aunque `ListingCard` no las dibuje, y es a propósito: esta consulta
    // usa como origen la geolocalización del PROPIO anuncio y no lo excluye, así que el que
    // se está mirando vuelve en sus propios resultados. Y como `combinedResourceObjects`
    // (`util/data.js`) reemplaza `variants` entero, pedir menos aquí le quita las escaladas a
    // las fotos que la ficha ya había guardado. Se quitaron el 3-11-2026 y el visor de fotos
    // se quedó sin nada que dibujar en producción.
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    'fields.listing': [
      'title',
      'description',
      'geolocation',
      'price',
      'createdAt',
      'availabilityPlan',
      'publicData.score',
      'publicData.capacity',
      'publicData.Regulation',
      'publicData.tag',
      'publicData.hostDetails',
      'publicData.extraItems',
      'publicData.capacityRanges',
      'publicData.extras',
      'publicData.priceRange',
      'publicData.searchListingType',
      'publicData.hasGarden',
      'publicData.poolMonths',
      'publicData.poolOffered',
      'publicData.gardenTitle',
      'publicData.gardenDescription',
      'publicData.gardenPriceRange',
      'publicData.gardenImageOrder',
      'publicData.hiddenImageIds',
      'publicData.gardenPoolUseExtra',
      // Espacios interiores: sección de la ficha y extras de la reserva (docs/indoor-spaces.md).
      'publicData.indoorSpaces',
      'publicData.listingType',
      'publicData.unitType',
      'publicData.firstBookingDiscountEnabled',
      'publicData.maxGuest',
      'publicData.minGuest',
      'publicData.minimumBookingHours',
      'publicData.minBookingPrice',
      'publicData.minAdvanceHours',
      'publicData.privacy',
      'publicData.poolMaintenance',
      'publicData.bathrooms',
      'publicData.ownerPresent',
      'publicData.transactionProcessAlias',
      'publicData.tiempo_respuesta',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    // ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    // ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    expand: true,
  };

  return mirrorOrSharetribe({
    mode: mirrorListingMode(),
    label: 'similar-listings',
    params: queryParams,
    config,
    fromSharetribe: () => sdk.listings.query(queryParams),
    fromMirror: ssrApiBaseUrl => searchMirrorListings(queryParams, ssrApiBaseUrl),
  })
    .then(res => {
      const listings = res.data.data;
      const listingIds = resultIds(listings);
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(res, sanitizeConfig));
      dispatch(fetchSimilarListingsSucess(listingIds));
      return listingIds;
    })
    .catch(e => {
      dispatch(fetchSimilarListingsError(storableError(e)));
      throw e;
    });
};

export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  return Promise.all([dispatch(showListing(listingId, config))]).then(response => {
    const listing = response[0]?.data?.data;
    const listingGeoLocation = listing && listing.id && listing.attributes.geolocation;

    const authorId = listing?.relationships?.author?.data?.id;

    dispatch(fetchReviews(listingId, authorId, config));

    dispatch(fetchSimilarListingsOnLocation(listingGeoLocation, config));
    const seoLandingPages = dispatch(
      fetchSeoLandingPagesOfCity(listing?.attributes?.publicData?.city, config)
    );
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    if (isBookingProcessAlias(transactionProcessAlias)) {
      // Fetch timeSlots.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }
    return seoLandingPages.then(() => response);
  });
};
